import type { ReactElement } from 'react';
import { MetaStatus } from '@gr/core/content/posts/meta-status';
import { PublicationStatus, type PublicationDto } from '@gr/core/distribution/publishing/dto';
import { zonedToUtc } from '@gr/core/time/zoned-time';
import { BlogPermission } from '@gr/core/accounts/workspaces/permissions';
import { useConfig, useServices, useT } from '../../../app/di';
import { fromLocalInputValue, toLocalInputValue } from '../../../shared/format';
import { useAction, useResource } from '../../../shared/hooks';
import { Alert } from '../../../ui-kit/Alert/Alert';
import { Card } from '../../../ui-kit/Card/Card';
import { useBlog } from '../../accounts/workspaces';
import { PlannedPublications } from './PlannedPublications/PlannedPublications';
import { PlanningForm, PlanningMode } from './PlanningForm/PlanningForm';

export interface PostPlanningPanelProps {
  postId: string;
  meta: MetaStatus;
  /** Публикации поста — загружает страница поста одним запросом на обе панели. */
  publications: readonly PublicationDto[];
  /** План изменился: страница перечитывает публикации и пост. */
  onChanged(): Promise<void>;
}

// SCH-UC-2, SCH-UC-5, SCH-UC-6, SCH-RULE-1, SCH-RULE-3
export function PostPlanningPanel({ postId, meta, publications, onChanged }: PostPlanningPanelProps): ReactElement {
  const { api } = useServices();
  const { ui } = useConfig();
  const { t, te } = useT();
  const { blog, can } = useBlog();
  const schemes = useResource(() => api.scheduling.schemes(blog.id), [api, blog.id]);
  const change = useAction((run: () => Promise<{ success: boolean; messages: string[]; data: unknown }>) => run());
  const toUtc = (local: string): string => fromLocalInputValue(local, blog.timezone, zonedToUtc) ?? '';

  const planned = publications.filter((p) => p.status === PublicationStatus.Planned);
  const editable = can(BlogPermission.Plan);
  const refresh = onChanged;

  const content = (): ReactElement | null => {
    if (planned.length > 0) {
      return (
        <div className="u-stack">
          <strong>{planned.some((p) => p.queueItemId) ? t('scheduling.panel.inQueue') : t('scheduling.panel.planned')}</strong>
          <PlannedPublications
            publications={planned}
            timezone={blog.timezone}
            editable={editable}
            pending={change.pending}
            onReschedule={async (publication, local) => {
              const ok = (await change.run(() => api.scheduling.reschedule(blog.id, publication.id, toUtc(local)))).success;
              if (ok) await refresh();
              return ok;
            }}
            onUnschedule={async (publication) => {
              if ((await change.run(() => api.scheduling.unschedule(blog.id, publication.id))).success) await refresh();
            }}
          />
        </div>
      );
    }
    if (meta !== MetaStatus.Ready) return <p className="u-muted">{t('scheduling.panel.notReady')}</p>;
    if (!editable) return null;
    if ((schemes.data ?? []).length === 0) return <p className="u-muted">{t('scheduling.panel.noSchemes')}</p>;
    return (
      <PlanningForm
        schemes={schemes.data ?? []}
        timezone={blog.timezone}
        defaultAt={toLocalInputValue(new Date(Date.now() + ui.scheduling.defaultExactOffsetHours * 3600_000).toISOString(), blog.timezone)}
        onSubmit={async (mode, values) => {
          const result =
            mode === PlanningMode.Queue
              ? await api.scheduling.enqueue(blog.id, postId, values.schemeId)
              : await api.scheduling.scheduleExact(blog.id, postId, values.schemeId, toUtc(values.at));
          if (result.success) await refresh();
          return result;
        }}
      />
    );
  };

  return (
    <Card title={t('scheduling.panel.title')}>
      <div className="u-stack" data-testid="planning-panel">
        {change.error ? <Alert tone="danger">{te(change.error)}</Alert> : null}
        {content()}
      </div>
    </Card>
  );
}
